import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Building2, Receipt, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

const EMPTY = { type: "company", name: "", ico: "", dic: "", street: "", zip: "", city: "",
  contact_person: "", email: "", phone: "", note: "" };

// Stejná karta jako v ProjectEditDialog (Sec) — hlavička s brand ikonou,
// volitelná akce vpravo, pole uvnitř dostanou bg-card.
function Sec({ icon: Icon, title, action, children }) {
  return (
    <section className="bg-secondary/60 border border-border rounded-[14px] p-4 space-y-3.5 min-w-0 [&_input]:bg-card [&_textarea]:bg-card [&_input]:placeholder:text-muted-foreground/55 [&_textarea]:placeholder:text-muted-foreground/55">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading font-extrabold text-[12px] uppercase tracking-[0.07em]">
          {Icon && <Icon size={16} className="text-brand shrink-0" />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function InvestorEditDialog({ open, onOpenChange, investor, mode = "create", onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [touched, setTouched] = useState(false);
  const [ares, setAres] = useState("idle");      // idle | busy | ok | error
  const [aresMsg, setAresMsg] = useState("");
  const initial = useRef(JSON.stringify(EMPTY));

  useEffect(() => {
    if (!open) return;
    const init = mode === "create" ? { ...EMPTY } : { ...EMPTY, ...(investor || {}) };
    initial.current = JSON.stringify(init);
    setF(init); setSaved(false); setTouched(false); setAres("idle"); setAresMsg("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, investor?.id]);

  const set = (k) => (e) => { setF((p) => ({ ...p, [k]: e.target.value })); setSaved(false); };
  const isFirma = f.type === "company";
  const nameOk = f.name.trim().length > 0;
  const showErr = touched && !nameOk;
  const dirty = initial.current !== JSON.stringify(f);
  const icoDigits = (f.ico || "").replace(/\s/g, "");

  const setType = (type) => setF((p) => (type === "person"
    ? { ...p, type, ico: "", dic: "", street: "", zip: "", city: "", contact_person: "" }
    : { ...p, type }));

  const loadAres = async () => {
    setAres("busy"); setAresMsg("Hledám v registru…");
    try {
      const { data } = await api.get(`/investors/ares/${icoDigits}`);
      setF((p) => ({ ...p, name: data.name || p.name, dic: data.dic || p.dic,
        street: data.street || p.street, zip: data.zip || p.zip, city: data.city || p.city }));
      setAres("ok");
      setAresMsg(`✓ Načteno z registru${data.dic ? " · DIČ " + data.dic : ""}${data.city ? " · " + data.city : ""}`);
    } catch (e) {
      setAres("error");
      setAresMsg(formatApiError(e.response?.data?.detail) || "Registr neodpověděl — vyplň údaje ručně.");
    }
  };

  const save = async () => {
    if (!nameOk) { setTouched(true); return toast.error("Vyplňte jméno."); }
    setBusy(true);
    try {
      const payload = { ...f, name: f.name.trim() };
      if (mode === "create") await api.post("/investors", payload);
      else await api.patch(`/investors/${investor.id}`, payload);
      setSaved(true);
      toast.success(mode === "create" ? "Investor vytvořen." : "Investor upraven.");
      onSaved?.();
      setTimeout(() => onOpenChange(false), 900);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleOpenChange = async (next) => {
    if (next) return onOpenChange(true);
    if (dirty && !saved && !await ask("Máš neuložené změny. Zahodit?", { ok: "Zahodit změny", danger: true })) return;
    onOpenChange(false);
  };

  const chip = saved ? { t: "✓ Uloženo", c: "bg-success/15 text-success" }
    : dirty ? { t: "Neuložené změny", c: "bg-amber-500/15 text-amber-600 dark:text-amber-400" }
    : { t: "Beze změn", c: "bg-secondary text-muted-foreground" };
  const aresCls = ares === "ok" ? "text-success" : ares === "error" ? "text-destructive" : "text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="investor-dialog"
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); save(); } }}
        className="max-w-[700px] w-[calc(100%-1.5rem)] sm:w-full p-0 gap-0 flex flex-col max-h-[92vh] overflow-hidden">

        <div className="shrink-0 px-5 md:px-6 py-4 border-b border-border flex items-start justify-between gap-3 pr-14">
          <div className="min-w-0">
            <DialogTitle className="text-xl md:text-2xl font-black tracking-tight">
              {mode === "create" ? "Nový investor / klient" : "Upravit investora"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              Povinné je jen jméno — zbytek jde doplnit kdykoli.
            </p>
          </div>
          <span data-testid="investor-dirty-chip"
            className={`rounded-full px-3 py-1.5 text-[13px] font-bold whitespace-nowrap shrink-0 ${chip.c}`}>{chip.t}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4 min-w-0">

          <Sec icon={Building2} title="Kdo to je">
            <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3.5 items-end">
              <div>
                <Label>Typ</Label>
                <div className="mt-1 flex gap-1 p-1 bg-card border border-border rounded-[14px]" data-testid="investor-type">
                  {[["company", "Firma"], ["person", "Osoba"]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setType(v)} data-testid={`investor-type-${v}`}
                      className={`flex-1 h-10 rounded-[10px] text-[15px] transition-colors ${f.type === v
                        ? "bg-brand text-white font-bold" : "font-semibold text-muted-foreground hover:bg-secondary"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{isFirma ? "Název firmy" : "Jméno a příjmení"} <span className="text-brand">*</span></Label>
                <Input data-testid="investor-name" value={f.name} onChange={set("name")} autoFocus
                  placeholder={isFirma ? "Fresh Technology s.r.o." : "Jan Dvořák"}
                  className={`h-12 mt-1 w-full min-w-0 text-base font-semibold ${showErr ? "border-destructive" : ""}`} />
              </div>
            </div>
            {showErr && <p className="text-[13px] text-destructive">Bez jména ho neuložíš.</p>}
          </Sec>

          {isFirma && (
            <Sec icon={Receipt} title="Fakturační údaje" action={
              <Button type="button" variant="outline" onClick={loadAres}
                disabled={ares === "busy" || icoDigits.length !== 8}
                className="h-10 shrink-0 font-semibold" data-testid="investor-ares">
                {ares === "busy" ? "Hledám…" : "Načíst z ARES"}
              </Button>
            }>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <Label>IČO</Label>
                  <Input data-testid="investor-ico" value={f.ico} onChange={set("ico")} inputMode="numeric"
                    placeholder="27082440" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
                </div>
                <div>
                  <Label>DIČ</Label>
                  <Input data-testid="investor-dic" value={f.dic} onChange={set("dic")}
                    placeholder="CZ27082440" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
                </div>
              </div>
              <p className={`text-[13px] ${aresCls}`} data-testid="investor-ares-msg">
                {aresMsg || "Zadej IČO a doplní se název, DIČ i adresa."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.4fr] gap-3.5">
                <div>
                  <Label>Ulice a č. p.</Label>
                  <Input data-testid="investor-street" value={f.street} onChange={set("street")}
                    placeholder="Václavské náměstí 1" className="h-12 mt-1 w-full min-w-0" />
                </div>
                <div>
                  <Label>PSČ</Label>
                  <Input data-testid="investor-zip" value={f.zip} onChange={set("zip")} inputMode="numeric"
                    placeholder="110 00" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
                </div>
                <div>
                  <Label>Město</Label>
                  <Input data-testid="investor-city" value={f.city} onChange={set("city")}
                    placeholder="Praha 1" className="h-12 mt-1 w-full min-w-0" />
                </div>
              </div>
            </Sec>
          )}

          <Sec icon={Phone} title="Kontakt">
            {isFirma && (
              <div>
                <Label>Kontaktní osoba</Label>
                <Input data-testid="investor-contact" value={f.contact_person} onChange={set("contact_person")}
                  placeholder="Jan Dvořák" className="h-12 mt-1 w-full min-w-0" />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label>Telefon</Label>
                <Input data-testid="investor-phone" value={f.phone} onChange={set("phone")} inputMode="tel"
                  placeholder="+420 777 123 456" className="h-12 mt-1 w-full min-w-0 tabular-nums" />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input data-testid="investor-email" value={f.email} onChange={set("email")} inputMode="email"
                  placeholder={isFirma ? "fakturace@firma.cz" : "jan@email.cz"} className="h-12 mt-1 w-full min-w-0" />
              </div>
            </div>
            <div>
              <Label>Poznámka</Label>
              <Textarea data-testid="investor-note" value={f.note} onChange={set("note")} rows={2}
                placeholder="Splatnost, kdo schvaluje vícepráce, na co si dát pozor…" className="mt-1 w-full min-w-0" />
            </div>
          </Sec>
        </div>

        <div className="shrink-0 px-5 md:px-6 py-3.5 border-t border-border flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-muted-foreground">
            {saved ? "Uloženo." : dirty ? "Změny se uloží až tlačítkem." : "Nic zatím neměníš."}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}
              className="h-11 w-full sm:w-auto font-bold" data-testid="investor-cancel">Zrušit</Button>
            <Button onClick={save} disabled={busy || !nameOk} data-testid="investor-save"
              className={`h-11 w-full sm:w-auto font-bold ${saved ? "bg-success hover:bg-success/90 text-white" : ""}`}>
              {saved ? <><Check size={16} /> Uloženo</> : busy ? "Ukládám…" : mode === "create" ? "Vytvořit investora" : "Uložit změny"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
